// owned by WP-WORKSPACES · the viewer's pinned desks, for other parts of the frame (DESK-HOST's desk warm-up F3 and kept-frame eviction):
// the `pinned` section of the viewer's `workspaces` layout, in pin order, as `{ pageKey, space }`. One value per fact: the pinned row is that
// layout section (SPEC §11 names `pref:workspaces.pinned`; the PrefCatalog has no such key and the layout gives Undo, History, every device
// and the agent's `ui.pin_block`), so readers use this hook instead of a second copy in a preference.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi, usePermission } from "@ncp/api-client";
import type { EffectiveLayout } from "@ncp/assistant-sdk";
import { useWorkspace } from "../../app/workspace-context";

export interface PinnedDesk {
  pageKey: string;
  space: string | null;
}

/**
 * The pinned desks of a `workspaces` layout, in order (pins that are not desk tiles are left out). The same reading as `pinsOf` in
 * ./model.ts (section `pinned`, `app` widgets with `ref = "<space>:<page>"`), written out here because the frame loads this file with its
 * first screen and the model stays with the Workspaces screen's own chunk (bundle budget SH-22).
 */
export function pinnedDesks(layout: EffectiveLayout | null | undefined): PinnedDesk[] {
  const widgets = (layout?.sections ?? []).find((s) => s.id === "pinned")?.widgets ?? [];
  return widgets.flatMap((w): PinnedDesk[] => {
    if (w.type !== "app" || !w.ref) return [];
    const [space, page] = w.ref.includes(":") ? (w.ref.split(":", 2) as [string, string]) : [null, w.ref];
    return page ? [{ pageKey: page, space }] : [];
  });
}

/**
 * The viewer's pinned desks (React Query cache shared with the Workspaces screen: the same key as `useLayout("workspaces", { space })`,
 * so its writes and `ui.layout.changed` refresh it). The layout client loads with the first fetch, after the first paint.
 */
export function usePinnedDesks(options?: { enabled?: boolean }): { desks: PinnedDesk[]; loading: boolean } {
  const { workspaceKey } = useWorkspace();
  const api = useApi();
  const { can } = usePermission();
  const space = workspaceKey ?? null;
  const q = useQuery({
    queryKey: ["layouts", "layout", "workspaces", space, null, false] as const,
    queryFn: () => import("@ncp/assistant-sdk/layouts").then(({ LayoutsApi }) => new LayoutsApi(api).layout("workspaces", { space, preview: null, withMine: false })),
    // Desks are pinned in the AI workspace; without it (the content-blind DT Administrator) there is nothing to read.
    enabled: (options?.enabled ?? true) && can("assistant.use"),
    staleTime: 30_000,
    retry: 1,
  });
  const desks = useMemo(() => pinnedDesks(q.data ?? null), [q.data]);
  return { desks, loading: q.isLoading };
}
